import { useEffect } from 'react';
import { X } from 'lucide-react';
import { IconButton } from './Button';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: 'md' | 'lg' | 'xl';
}

const widthClasses = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

export function Drawer({ isOpen, onClose, title, children, width = 'lg' }: DrawerProps) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`relative ${widthClasses[width]} w-full bg-[var(--asi-bg)] shadow-2xl overflow-hidden animate-slide-in-right`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--asi-bg-tertiary)]">
          <h2 className="text-lg font-semibold text-[var(--asi-text)]">{title}</h2>
          <IconButton
            icon={<X className="w-5 h-5" />}
            label="Close"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100vh-65px)] p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
